import Avatar from "@/components/avatar";
import { Skeleton } from "@/components/other";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useVirtualizer } from "@tanstack/react-virtual";
import isEqual from "fast-deep-equal";
import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import type { ExternalInvitationsProps } from "./External";

interface ExternalsProps {
	tab: ExternalInvitationsProps["tab"];
	subTab: ExternalInvitationsProps["subTab"];
	listItems: any[];
	isEmpty: boolean;
	isLoading: boolean;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
	onChange: (
		e: React.ChangeEvent<HTMLInputElement>,
		val: any,
		type: "teams" | "contacts",
	) => void;
}

const Externals = ({
	tab,
	subTab,
	listItems,
	isEmpty,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	onChange,
}: ExternalsProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const prevTabRef = useRef(tab);
	const prevSubTabRef = useRef(subTab);

	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0,
		rootMargin: "100px",
	});

	const virtualItems = useMemo(() => {
		const items = [...listItems];
		if (isFetchingNextPage) {
			items.push(
				...Array.from({ length: 3 }, (_, i) => ({
					id: `skeleton-${i}`,
					isSkeleton: true,
				})),
			);
		}
		return items;
	}, [listItems, isFetchingNextPage]);

	const rowVirtualizer = useVirtualizer({
		count: virtualItems.length,
		overscan: 5,
		estimateSize: () => 58,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el?.getBoundingClientRect().height,
	});

	const rowVirtualizerItems = rowVirtualizer.getVirtualItems();

	const renderListItem = useCallback(
		(listItem: any) => {
			const isTeam = "type" in listItem && listItem.type === "team";
			const name = isTeam ? listItem.name : listItem.fullname;
			const id = listItem.id ?? listItem.aidbox_id;
			const isChecked = listItem.isChecked ?? false;

			if (!id) return null;

			return (
				<ListItemButton
					component="label"
					htmlFor={`invite-${id}`}
					classes={{
						root: isChecked ? "!bg-primary-light" : undefined,
					}}
				>
					<ListItemIcon>
						<Checkbox
							disableRipple
							edge="start"
							tabIndex={-1}
							inputProps={{
								"aria-labelledby": name || "Unknown",
							}}
							onChange={(e) =>
								onChange(e, listItem, isTeam ? "teams" : "contacts")
							}
							id={`invite-${id}`}
							icon={<RadioButtonUncheckedRoundedIcon />}
							checkedIcon={<CheckCircleIcon />}
							name={isTeam ? "teams[]" : "contacts[]"}
							value={id.toString()}
							checked={isChecked}
						/>
					</ListItemIcon>

					{!isTeam && (
						<ListItemAvatar>
							<Avatar
								alt={listItem.first_name || "User"}
								className="!bg-gray-light !text-dark"
								src={listItem.avatar ?? "/broken-image.jpg"}
							/>
						</ListItemAvatar>
					)}

					<ListItemText
						id={id.toString()}
						primary={name || "Unknown"}
						secondary={
							isTeam ? `${listItem.member_count || 0} team members` : undefined
						}
					/>
				</ListItemButton>
			);
		},
		[onChange],
	);

	useEffect(() => {
		if (tab !== "mpis" && inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [tab, inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Reset scroll position when tab or subTab changes
	useEffect(() => {
		const hasTabChanged = prevTabRef.current !== tab;
		const hasSubTabChanged = prevSubTabRef.current !== subTab;

		if (hasTabChanged || hasSubTabChanged) {
			// Reset scroll position to top
			if (scrollRef.current) {
				scrollRef.current.scrollTop = 0;
			}

			// Reset virtualizer scroll position
			rowVirtualizer.scrollToIndex(0);

			// Update refs with current values
			prevTabRef.current = tab;
			prevSubTabRef.current = subTab;
		}
	}, [tab, subTab, rowVirtualizer]);

	return (
		<div ref={scrollRef} className="h-full relative overflow-y-auto">
			<List
				className="!relative"
				style={{
					height: rowVirtualizer.getTotalSize(),
				}}
			>
				{/* Empty state for contacts */}
				{isEmpty && subTab === "contact" && (
					<ListItem disablePadding>
						<ListItemText
							primary="No contacts"
							classes={{
								primary: "!text-center !text-gray-200",
							}}
						/>
					</ListItem>
				)}

				{/* Empty state for teams */}
				{isEmpty && subTab === "team" && (
					<ListItem disablePadding>
						<ListItemText
							primary="No teams"
							classes={{
								primary: "!text-center !text-gray-200",
							}}
						/>
					</ListItem>
				)}

				{/* Virtual list items */}
				{rowVirtualizerItems.map((rowVirtualizerItem) => {
					const listItem = virtualItems[rowVirtualizerItem.index];
					if (!listItem) return null;

					return (
						<ListItem
							key={rowVirtualizerItem.key}
							data-index={rowVirtualizerItem.index}
							ref={rowVirtualizer.measureElement}
							disablePadding
							className="!absolute !top-0 !left-0 !w-full"
							style={{
								transform: `translateY(${rowVirtualizerItem.start}px)`,
							}}
						>
							{!listItem.isSkeleton ? (
								renderListItem(listItem)
							) : (
								<Skeleton className="w-full h-16 my-2" />
							)}
						</ListItem>
					);
				})}
			</List>

			{/* Intersection observer trigger for infinite scroll */}
			{hasNextPage && (
				<div
					ref={loadMoreRef}
					className="h-1"
					style={{
						position: "absolute",
						bottom: "200px",
						width: "100%",
					}}
				/>
			)}
		</div>
	);
};

export default memo(Externals, isEqual);
